import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { AxiosError } from "axios";
import {
  aiModelSortMap,
  CortexRegistryType,
  CreateAIModelRequestBody,
  CreateProcessRequestBody,
  DeployVertexAIModelRequestBody,
  DeployVertexAIModelResponseBody,
  GetAIModelDetailsResponse,
  GetDeploymentConfigAIModelResponseBody,
  ListAIModelsResponse,
  ListCortexRegistryAIModelsResponseBody,
  OrderBy,
  Process,
  UpdateAIModelRequestBody,
  VertexAIModelSourceType,
} from "common";

// Helper to convert frontend filters to backend filter format
// Backend format: "type:eq=1,registry:eq=1"
const buildFilterString = (
  filters: Array<{ id: string; value: unknown }>
): string => {
  const filterParts: string[] = [];

  filters.forEach(filter => {
    if (
      filter.value === "" ||
      filter.value === null ||
      filter.value === undefined
    ) {
      return;
    }

    // Map frontend filter IDs to backend filter names and operators
    const filterMap: Record<string, { field: string; operator: string }> = {
      type: { field: "type", operator: "eq" },
      registry: { field: "registry", operator: "eq" },
      name: { field: "name", operator: "like" }, // Use like for partial name matching
      ownerId: { field: "ownerId", operator: "eq" },
    };

    const filterConfig = filterMap[filter.id];
    if (filterConfig) {
      // For like operator, wrap value in % for SQL LIKE pattern
      const filterValue =
        filterConfig.operator === "like" ? `%${filter.value}%` : filter.value;
      filterParts.push(
        `${filterConfig.field}:${filterConfig.operator}=${filterValue}`
      );
    }
  });

  return filterParts.join(",");
};

// Helper to convert frontend sort to backend sort
const getSortField = (sortId: string): keyof typeof aiModelSortMap => {
  // Map frontend sort IDs to backend sort fields
  const sortMap: Record<string, keyof typeof aiModelSortMap> = {
    dateRegistered: "createdAt",
    dateUsed: "updatedAt",
    name: "name",
    type: "type",
    registry: "registry",
    createdAt: "createdAt",
    updatedAt: "updatedAt",
  };

  return sortMap[sortId] || "createdAt";
};

export const fetchAIModelsList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter: Array<{ id: string; value: unknown }>
): Promise<ListAIModelsResponse> => {
  try {
    const baseUrl = "/api/v1/cortex/ai-model/list";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    // Add sort
    if (sort && sort.length > 0) {
      const sortField = getSortField(sort[0].id);
      queryParams.append("sort", sortField);
      queryParams.append("order", sort[0].desc ? OrderBy.Desc : OrderBy.Asc);
    } else {
      queryParams.append("sort", "createdAt");
      queryParams.append("order", OrderBy.Desc);
    }

    // Add filter
    const filterString = buildFilterString(filter);
    if (filterString) {
      queryParams.append("filter", filterString);
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get<ListAIModelsResponse>(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useAIModelsList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): UseQueryResult<ListAIModelsResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/v1/cortex/ai-model/list", take, skip, sort, filter],
    queryFn: () => fetchAIModelsList(take, skip, sort, filter),
    retry: false,
    enabled,
  });
};

export const fetchAIModel = async (
  id: number
): Promise<GetAIModelDetailsResponse> => {
  try {
    const res = await axios.get<GetAIModelDetailsResponse>(
      `/api/v1/cortex/ai-model/${id}`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetAIModel = (
  id: number,
  enabled: boolean = true,
  refetchInterval?: number | false
): UseQueryResult<GetAIModelDetailsResponse, Error> => {
  return useQuery({
    queryKey: ["/api/v1/cortex/ai-model", id],
    queryFn: () => fetchAIModel(id!),
    retry: false,
    enabled: enabled && id !== undefined,
    refetchInterval,
  });
};

export const useCreateAIModel = (): UseMutationResult<
  CreateAIModelRequestBody,
  Error,
  CreateAIModelRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: CreateAIModelRequestBody) => {
      const res = await axios.post("/api/v1/cortex/ai-model", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "ai-model-created",
        message: "AI Model Successfully Created",
        details: "You can now use this model",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "ai-model-creation-failed",
        message: "AI Model Creation Failed",
        details: error.response?.data?.message ?? "Model could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateAIModel = (): UseMutationResult<
  GetAIModelDetailsResponse,
  AxiosError | ErrorNotification,
  { id: number; payload: UpdateAIModelRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: UpdateAIModelRequestBody;
    }) => {
      const res = await axios.put(
        `/api/v1/cortex/ai-model/${params.id}`,
        params.payload
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "ai-model-updated",
        message: "AI Model Successfully Updated",
        details: "Changes have been saved",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "ai-model-update-failed",
        message: "AI Model Update Failed",
        details: error.response?.data?.message ?? "Model could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchVertexModels = async (
  keyword?: string,
  registry?: CortexRegistryType,
  sourceType?: VertexAIModelSourceType
): Promise<ListCortexRegistryAIModelsResponseBody> => {
  try {
    const queryParams = new URLSearchParams();
    if (keyword) {
      queryParams.append("keyword", keyword);
    }

    queryParams.append(
      "registry",
      String(registry ?? CortexRegistryType.VertexAI)
    );

    if (sourceType) {
      queryParams.append(
        "private",
        sourceType === VertexAIModelSourceType.ModelRegistry ? "true" : "false"
      );
    }

    const url = `/api/v1/cortex/models${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const res = await axios.get<ListCortexRegistryAIModelsResponseBody>(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useCortexModelsList = (
  keyword?: string,
  registry?: CortexRegistryType,
  sourceType?: VertexAIModelSourceType,
  enabled: boolean = true
): UseQueryResult<
  ListCortexRegistryAIModelsResponseBody,
  AxiosError | ErrorNotification
> => {
  return useQuery({
    queryKey: ["/api/v1/cortex/models", keyword, registry, sourceType],
    queryFn: () => fetchVertexModels(keyword, registry, sourceType),
    retry: false,
    enabled,
  });
};

export const useDeleteAIModel = (): UseMutationResult<
  void,
  AxiosError | ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`/api/v1/cortex/ai-model/${id}`);
      return;
    },
    onSuccess: (): void => {
      setNotification({
        id: "ai-model-deleted",
        message: "AI Model Successfully Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.message ?? "Failed to delete model";
      setNotification({
        id: "ai-model-delete-failed",
        message: "Delete AI Model Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDuplicateAIModel = (): UseMutationResult<
  Process,
  AxiosError | ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (processId: number) => {
      const res = await axios.post(`/api/v1/process/${processId}/duplicate`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "ai-model-duplicate-success",
        message: "AI Model Successfully Duplicated",
        details: "Duplicated AI Model is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "ai-model-duplicate-failed",
        message: "AI Model Duplication Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDuplicateModelSkill = (): UseMutationResult<
  Process,
  AxiosError | ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (processId: number) => {
      const res = await axios.post(`/api/v1/process/${processId}/duplicate`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "model-skill-duplicate-success",
        message: "Model Skill Successfully Duplicated",
        details: "Duplicated Model Skill is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "model-skill-duplicate-failed",
        message: "Model Skill Duplication Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateAIModelRevision = (): UseMutationResult<
  Process,
  AxiosError | ErrorNotification,
  {
    processId: number;
    payload: CreateProcessRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: CreateProcessRequestBody;
    }) => {
      const res = await axios.post(`/api/v1/process/${processId}`, payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "create-ai-model-revision-success",
        message: "AI Model Successfully Revised",
        details: "New AI Model revision is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-ai-model-revision-failed",
        message: "AI Model Revision Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateModelSkillRevision = (): UseMutationResult<
  Process,
  AxiosError | ErrorNotification,
  {
    processId: number;
    payload: CreateProcessRequestBody;
  }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: CreateProcessRequestBody;
    }) => {
      const res = await axios.post(`/api/v1/process/${processId}`, payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "create-model-skill-revision-success",
        message: "Model Skill Successfully Revised",
        details: "New AI Model revision is now available",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "create-model-skill-revision-failed",
        message: "Model Skill Revision Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteAIModelRevision = (): UseMutationResult<
  void,
  AxiosError | ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (processId: number): Promise<void> => {
      await axios.delete(`/api/v1/process/${processId}`);
    },
    onSuccess: (): void => {
      setNotification({
        id: "delete-ai-model-revision-success",
        message: "AI Model Revision Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-ai-model-revision-failed",
        message: "AI Model Revision Deletion Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to delete the revision",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchAIModelDetails = async (
  id: string
): Promise<GetAIModelDetailsResponse> => {
  try {
    const res = await axios.get(`/api/v1/cortex/ai-model/process/${id}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useFetchAIModelDetails = (): UseMutationResult<
  GetAIModelDetailsResponse,
  AxiosError | ErrorNotification,
  string
> => {
  return useMutation({
    mutationFn: async (id: string) => {
      return await fetchAIModelDetails(id);
    },
  });
};

// Deployment Config API
export const fetchDeploymentConfig = async (
  id: number
): Promise<GetDeploymentConfigAIModelResponseBody> => {
  try {
    const res = await axios.get<GetDeploymentConfigAIModelResponseBody>(
      `/api/v1/cortex/ai-model/${id}/deployment-config`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useDeploymentConfig = (
  id: number | undefined,
  enabled: boolean = true
): UseQueryResult<
  GetDeploymentConfigAIModelResponseBody,
  AxiosError | ErrorNotification
> => {
  return useQuery({
    queryKey: ["/api/v1/cortex/ai-model/deployment-config", id],
    queryFn: () => fetchDeploymentConfig(id!),
    retry: false,
    enabled: enabled && id !== undefined,
  });
};

// Deploy AI Model API
export const useDeployAIModel = (): UseMutationResult<
  DeployVertexAIModelResponseBody,
  AxiosError | ErrorNotification,
  { id: number; payload: DeployVertexAIModelRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: DeployVertexAIModelRequestBody;
    }) => {
      const res = await axios.post(
        `/api/v1/cortex/ai-model/${params.id}/deploy`,
        params.payload
      );
      return res.data;
    },
    onSuccess: (_, variables): void => {
      setNotification({
        id: "ai-model-deploy-success",
        message: "AI Model Deployment Started",
        details: "The model deployment has been initiated successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model", variables.id],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "ai-model-deploy-failed",
        message: "AI Model Deployment Failed",
        details: error.response?.data?.message ?? "Failed to deploy the model",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

// Undeploy AI Model API
export const useUndeployAIModel = (): UseMutationResult<
  DeployVertexAIModelResponseBody,
  AxiosError | ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.post(`/api/v1/cortex/ai-model/${id}/undeploy`);
      return res.data;
    },
    onSuccess: (_, id): void => {
      setNotification({
        id: "ai-model-undeploy-success",
        message: "AI Model Undeployment Started",
        details: "The model undeployment has been initiated successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/ai-model", id],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "ai-model-undeploy-failed",
        message: "AI Model Undeployment Failed",
        details:
          error.response?.data?.message ?? "Failed to undeploy the model",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
